import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { $initialCurrenciesAvailableStore, $dashboardAccountSummaryStore, $initialAccountsStore } from '@lib/store';
import { AccountUtils, cn, DateUtils, PaletteUtils } from '@shared/utils';
import { AccountType } from '@shared/api/rest/accounts';
import { PaymentItems } from './paymentItems';
import { MonthlyTransaction } from './monthlyTransaction';
import { YearlyTransaction } from './yearlyTransaction';
import { NoData, Select, Spinner, Text } from '@shared/widgets';

export const PaymentTab = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardsAccountSummaryStore = useUnit($dashboardAccountSummaryStore.store);

  // const [searchText, setSearchText] = useState<string>('');
  const [currency, setCurrency] = useState<string>();

  const { t, i18n } = useTranslation('dashboard');

  useEffect(() => {
    $dashboardAccountSummaryStore.events.update({ Currency: currency });
  }, []);

  const currentMonth = useMemo(() => DateUtils.getCurrentMonth({ locale: i18n.language || 'en' }), [i18n.language]);
  const paymentAccounts = useMemo(() => accountStore.data.filter((account) => account?.type === AccountType.Payment), [accountStore.data]);
  const minimalDate = useMemo(() => dayjs.min(paymentAccounts.map((acc) => dayjs(acc.dateCreated)))?.format(), [paymentAccounts]);
  const filteredAccounts = useMemo(
    () => AccountUtils.filterDashboardAccounts(dashboardsAccountSummaryStore.data, { currency }),
    [dashboardsAccountSummaryStore.data, currency],
  );
  const groupedAccounts = useMemo(() => AccountUtils.groupedDashboardAccounts(filteredAccounts), [filteredAccounts]);

  if (dashboardsAccountSummaryStore.loading) return <Spinner className={cn(PaletteUtils.isVcfcPalette && 'my-10')} />;
  return (
    <>
      <div
        className={cn(
          'flex flex-col',
          PaletteUtils.isInsigniaPalette && 'gap-6',
          PaletteUtils.isVcfcPalette && [
            'bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
            'max-md:rounded-xl max-md:p-4',
          ],
        )}
      >
        <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && ['mb-4 items-center', 'max-md:flex-col max-md:items-start'])}>
          <Text weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
            {t('payment.title')}
            <span className={cn('ml-2 text-pearl-dark-gray', PaletteUtils.isVcfcPalette && ['ml-3 text-base font-normal', 'max-md:opacity-0'])}>
              {currentMonth}
            </span>
          </Text>
          <div className={cn('flex gap-4', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
            {/* <SearchInput
              className={cn('flex-basis-0 flex-shrink-0 flex-grow', PaletteUtils.isVcfcPalette && ['flex-auto', 'max-md:w-full'])}
              value={searchText}
              onChange={setSearchText}
            /> */}
            <Select
              className={cn('min-w-[160px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
              items={currenciesAvailableStore.data}
              nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
              value={currency}
              onChange={(val) => setCurrency(val!)}
            />
          </div>
        </div>
        <div className='flex flex-col gap-6'>
          {groupedAccounts.length > 0 ? (
            groupedAccounts.map((group, idx) => (
              <PaymentItems key={`${idx}_${group.mainAccount.id}`} mainAccount={group.mainAccount} subAccounts={group.subAccounts} />
            ))
          ) : (
            <NoData />
          )}
        </div>
      </div>
      <MonthlyTransaction minimalYear={dayjs(minimalDate).year()} minimalMonth={dayjs(minimalDate).month() + 1} />
      <YearlyTransaction minimalYear={dayjs(minimalDate).year()} />
    </>
  );
};
